import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import moment from "moment";

import { enGB } from "date-fns/locale/en-GB";
registerLocale("en-GB", enGB);

import cn from "classnames";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/liveOps/tournaments/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import RangeInput from "components/inputs/RangeInput/RangeInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import { ImageUpload } from "components/imageUpload";
import { getDatePickerFormats, useTimeFormat } from "helpers/timeFormat";

interface TournamentsMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
}

const TournamentsMainTab: React.FC<TournamentsMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  readOnly
}) => {
  const { t } = useTranslation();
  const timeFormat = useTimeFormat();
  const dispatch = useAppDispatch();
  const payload = useAppSelector((store) => store.tournaments.payload);
  const statusList = useAppSelector((store) => store.tournaments.statusList);
  const isListStatusFetched = useAppSelector(
    (store) => store.tournaments.isListStatusFetched,
  );

  const [optionsStatus, setOptionsStatus] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    if (isListStatusFetched && statusList.length > 0) {
      const array = statusList.map((el: any) => {
        return {
          value: el.id,
          label: el.title,
        };
      });
      setOptionsStatus(array);
    }
  }, [isListStatusFetched, statusList]);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  const safeMax = payload?.maxMembersCount ? payload.maxMembersCount / 2 : 2;

  const safeValue = Math.min(payload?.botsCount ?? 0, safeMax);
  const datePickerFormats = getDatePickerFormats(timeFormat);

  return (
    <div className="bonus-form-content tournament-form-content">
      <div className="tournament-main-top">
        <div className="bonus-form-box">
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.title ?? ""}
              placeholder={t("title")}
              label={t("title")}
              type="text"
              id="tournament-title"
              onChange={(e) => {
                handleData("title", e);
                clearFieldError("title");
              }}
              hasError={Boolean(fieldErrors?.["title"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              maxCodePoints={2500}
              value={payload?.description ?? ""}
              placeholder={t("enterText")}
              label={t("description")}
              type="textarea"
              rows={8}
              id="tournament-description"
              onChange={(e) => {
                handleData("description", e);
                clearFieldError("description");
              }}
              hasError={Boolean(fieldErrors?.["description"])}
              disabled={readOnly}
            />
          </div>
          <div className="tournament-left-details">
            <div className="bonus-form-box--item">
              <DefaultDropdown
                value={payload?.statusId}
                placeholder={t("selectStatus")}
                label={t("status")}
                options={optionsStatus}
                disabled={
                  optionsStatus.length === 0
                  || !isListStatusFetched
                  || readOnly
                }
                onSelect={(e) => {
                  handleData("statusId", e);
                  clearFieldError("statusId");
                }}
                hasError={Boolean(fieldErrors?.["statusId"])}
              />
            </div>
            <div className="bonus-form-box--item">
              <InnerInput
                value={payload?.maxMembersCount?.toString() ?? ""}
                placeholder={t("maxMembersCount")}
                label={t("maxMembersCount")}
                type="number"
                id="tournament-max-members"
                onChange={(e) => {
                  handleData("maxMembersCount", e);
                  clearFieldError("maxMembersCount");
                }}
                hasError={Boolean(fieldErrors?.["maxMembersCount"])}
                disabled={readOnly}
              />
            </div>
            <div className="bonus-form-box--item">
              <div
                className={cn("date-picker-wrapper", {
                  "has-error": fieldErrors?.["startedAt"],
                })}
              >
                <div className="title">{t("startedAt")}</div>
                <DatePicker
                  customInput={<ReadOnlyInput />}
                  locale="en-GB"
                  showTimeSelect
                  timeFormat={datePickerFormats.timeFormat}
                  placeholderText={datePickerFormats.placeholder}
                  dateFormat={datePickerFormats.dateFormat}
                  selected={
                    payload?.startedAt
                      ? moment(payload.startedAt, "DD.MM.YYYY HH:mm", true).toDate()
                      : null
                  }
                  onChange={(date) => {
                    if (!date) return;
                    handleData("startedAt", moment(date).format("DD.MM.YYYY HH:mm"));
                    clearFieldError("startedAt");
                  }}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="bonus-form-box--item">
              <div
                className={cn("date-picker-wrapper", {
                  "has-error": fieldErrors?.["finishedAt"],
                })}
              >
                <div className="title">{t("finishedAt")}</div>
                <DatePicker
                  customInput={<ReadOnlyInput />}
                  locale="en-GB"
                  showTimeSelect
                  timeFormat={datePickerFormats.timeFormat}
                  placeholderText={datePickerFormats.placeholder}
                  dateFormat={datePickerFormats.dateFormat}
                  selected={
                    payload?.finishedAt
                      ? moment(payload.finishedAt, "DD.MM.YYYY HH:mm", true).toDate()
                      : null
                  }
                  onChange={(date) => {
                    if (!date) return;
                    handleData("finishedAt", moment(date).format("DD.MM.YYYY HH:mm"));
                    clearFieldError("finishedAt");
                  }}
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bonus-form-box">
          <div className="bonus-form-img tournament-main-images">
            <div className="bonus-images">
              <div className="bonus-image-item desktop">
                <ImageUpload
                  label="Desktop"
                  labelClassName="bonus-main-form-label"
                  image={payload?.image}
                  allowMp4
                  size="large"
                  onAddImage={(file) => {
                    handleData("image", "", file);
                    clearFieldError("image");
                  }}
                  isActive={true}
                  disabled={readOnly}
                  hasError={!!fieldErrors?.["image"]}
                />
              </div>

              <div className="bonus-images-secondary">
                <div className="bonus-image-item mobile">
                  <ImageUpload
                    label="Mobile"
                    labelClassName="bonus-main-form-label"
                    image={payload?.imageSmall}
                    allowMp4
                    size="large"
                    type="landscape"
                    aspect="21:9"
                    onAddImage={(file) => {
                      handleData("imageSmall", "", file);
                      clearFieldError("imageSmall");
                    }}
                    isActive={true}
                    disabled={readOnly}
                    hasError={!!fieldErrors?.["imageSmall"]}
                  />
                </div>

                <div className="bonus-image-item bonus-portrait">
                  <ImageUpload
                    label="Portrait"
                    labelClassName="bonus-main-form-label"
                    image={payload?.portraitImage}
                    allowMp4
                    size="large"
                    type="portrait"
                    aspect="9:16"
                    onAddImage={(file) => {
                      handleData("portraitImage", "", file);
                      clearFieldError("portraitImage");
                    }}
                    isActive={true}
                    disabled={readOnly}
                    hasError={!!fieldErrors?.["portraitImage"]}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bonus-form-switcher">
            <SwitchBtn
              activeTxt={t("active")}
              notActiveTxt={t("hidden")}
              isActive={!!payload?.isActive}
              handleClick={() => {
                handleData(
                  "isActive",
                  payload?.isActive ? !payload?.isActive : true,
                );
                clearFieldError("isActive");
              }}
              hasError={Boolean(fieldErrors?.["isActive"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item tournament-bots-count">
            <RangeInput
              label={t("botsCount")}
              values={[safeValue]}
              max={safeMax}
              min={0}
              step={1}
              disabled={!payload?.maxMembersCount || readOnly}
              onChange={(e) => {
                handleData("botsCount", e[0]);
                clearFieldError("botsCount");
              }}
              hasError={
                Boolean(fieldErrors?.["botsCount"])
                && Boolean(payload?.maxMembersCount)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentsMainTab;
